const dateFormatter = new Intl.DateTimeFormat("en-US", {
  year: "numeric",
  month: "long",
  day: "numeric",
});

const shortDateFormatter = new Intl.DateTimeFormat("en-US", {
  year: "numeric",
  month: "short",
  day: "numeric",
});

const monthFormatter = new Intl.DateTimeFormat("en-US", {
  year: "numeric",
  month: "long",
});

/** "January 2026" — the section heading on the ratings ledger. */
export function formatMonth(iso: string | null): string {
  if (!iso) return "";
  return monthFormatter.format(new Date(iso));
}

/** Day of month, zero-padded, for the ledger's left gutter. */
export function formatDayOfMonth(iso: string | null): string {
  if (!iso) return "";
  return String(new Date(iso).getDate()).padStart(2, "0");
}

export function formatDate(iso: string | null): string {
  if (!iso) return "";
  return dateFormatter.format(new Date(iso));
}

export function formatShortDate(iso: string | null): string {
  if (!iso) return "";
  return shortDateFormatter.format(new Date(iso));
}

/** "3 days ago" for recent items, an absolute date once it stops being useful. */
export function formatRelative(iso: string): string {
  const then = new Date(iso).getTime();
  const seconds = Math.round((Date.now() - then) / 1000);

  if (seconds < 60) return "just now";

  const units: [Intl.RelativeTimeFormatUnit, number][] = [
    ["minute", 60],
    ["hour", 3600],
    ["day", 86_400],
  ];

  const formatter = new Intl.RelativeTimeFormat("en-US", { numeric: "auto" });

  for (const [unit, size] of units.toReversed()) {
    if (seconds >= size) {
      return formatter.format(-Math.floor(seconds / size), unit);
    }
  }

  return shortDateFormatter.format(then);
}

/** `8.5` -> `8.5`, `8` -> `8`. Keeps the half-point without a trailing `.0`. */
export function formatRating(rating: number | null): string | null {
  if (rating === null) return null;
  return Number.isInteger(rating) ? String(rating) : rating.toFixed(1);
}

export function pluralize(count: number, singular: string, plural?: string) {
  return count === 1 ? singular : (plural ?? `${singular}s`);
}

/**
 * An ISO instant as `YYYY-MM-DD`, for a date input.
 *
 * Read in UTC, to match how `fromDateInput` writes it. The pair have to agree
 * on a timezone or the round trip is not stable: built from local parts and
 * read back as UTC, a date walks forward or back by a day every time the review
 * is opened and saved, and which way depends on where the editor is sitting.
 *
 * A row dated automatically before this field existed can be a few hours either
 * side of midnight and so show here as the neighbouring day. It settles the
 * first time the date is saved by hand.
 */
export function toDateInput(iso: string | null): string {
  if (!iso) return "";

  const when = new Date(iso);
  if (Number.isNaN(when.getTime())) return "";

  const pad = (part: number) => String(part).padStart(2, "0");
  return `${when.getUTCFullYear()}-${pad(when.getUTCMonth() + 1)}-${pad(when.getUTCDate())}`;
}

/**
 * Back to an ISO instant: midday UTC on the chosen day.
 *
 * The date a reader sees is formatted in *their* timezone, so the stored
 * instant decides which day that lands on. Midday UTC is the middle of the
 * range, which leaves twelve hours of slack in both directions and puts every
 * reader from Hawaii to Japan on the day that was actually picked.
 *
 * Midday *local* is the version of this that looks right and is not: from New
 * York it is 16:00 UTC, which is already the next morning in Auckland and
 * Tokyo. Only UTC+12 and beyond — New Zealand, Fiji, Samoa — are still a day
 * ahead here, and no single instant can satisfy all of them at once, because
 * the inhabited timezones span twenty-six hours and a day is twenty-four.
 */
export function fromDateInput(value: string): string | null {
  const [year, month, day] = value.split("-").map(Number);
  if (!year || !month || !day) return null;

  return new Date(Date.UTC(year, month - 1, day, 12, 0, 0)).toISOString();
}
